import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { submitContactForm, trackButtonClick } from '../services/api';
import { Button } from './ui/button';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    interested_in: '',
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    // Track button click
    trackButtonClick('contact-form-submit', 'Send Inquiry', '/');

    const result = await submitContactForm(formData);

    if (result.success) {
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '', interested_in: '' });
    } else {
      setStatus('error');
      setErrorMessage(result.error || 'Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[var(--bg-card)] rounded-2xl p-8 border border-[var(--border-subtle)] text-center"
        data-testid="contact-form-success"
      >
        <CheckCircle className="w-16 h-16 text-[var(--accent-teal)] mx-auto mb-4" />
        <h3 className="font-display text-2xl font-semibold text-[var(--text-primary)] mb-2">
          Message Sent!
        </h3>
        <p className="text-[var(--text-muted)]">
          Thank you for reaching out. We'll get back to you within 24 hours.
        </p>
        <Button
          onClick={() => setStatus('idle')}
          variant="outline"
          className="mt-6"
          data-testid="send-another-btn"
        >
          Send Another Message
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.form
      id="contact-form"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.2 }}
      onSubmit={handleSubmit}
      className="bg-[var(--bg-card)] rounded-2xl p-6 md:p-8 border border-[var(--border-subtle)]"
      data-testid="contact-form"
    >
      <h3 className="font-display text-xl font-semibold text-[var(--text-primary)] mb-6 text-center">
        Or Send Us a Message
      </h3>

      {status === 'error' && (
        <div className="flex items-center gap-2 text-red-400 bg-red-400/10 rounded-lg p-3 mb-4" data-testid="contact-form-error">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{errorMessage}</span>
        </div>
      )}

      <div className="space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-[var(--text-muted)] mb-1.5">
            Your Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="John Doe"
            className="w-full px-4 py-3 rounded-lg bg-[var(--bg-page)] border border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--accent-teal)]/50 focus:border-[var(--accent-teal)] transition-all"
            data-testid="contact-name-input"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-[var(--text-muted)] mb-1.5">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
            className="w-full px-4 py-3 rounded-lg bg-[var(--bg-page)] border border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--accent-teal)]/50 focus:border-[var(--accent-teal)] transition-all"
            data-testid="contact-email-input"
          />
        </div>

        {/* Phone (Optional) */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-[var(--text-muted)] mb-1.5">
            Phone Number <span className="text-[var(--text-muted)]/50">(optional)</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+1 (555) 123-4567"
            className="w-full px-4 py-3 rounded-lg bg-[var(--bg-page)] border border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--accent-teal)]/50 focus:border-[var(--accent-teal)] transition-all"
            data-testid="contact-phone-input"
          />
        </div>

        {/* Interested In */}
        <div>
          <label htmlFor="interested_in" className="block text-sm font-medium text-[var(--text-muted)] mb-1.5">
            Interested In
          </label>
          <select
            id="interested_in"
            name="interested_in"
            value={formData.interested_in}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg bg-[var(--bg-page)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-teal)]/50 focus:border-[var(--accent-teal)] transition-all"
            data-testid="contact-interest-select"
          >
            <option value="">Select a program...</option>
            <option value="individual">Individual Coaching</option>
            <option value="corporate">Corporate Training</option>
            <option value="workshop">Workshop/Bootcamp</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-[var(--text-muted)] mb-1.5">
            Your Message *
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={4}
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your communication goals..."
            className="w-full px-4 py-3 rounded-lg bg-[var(--bg-page)] border border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--accent-teal)]/50 focus:border-[var(--accent-teal)] transition-all resize-none"
            data-testid="contact-message-input"
          />
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={status === 'loading'}
          className="w-full btn-primary py-4 text-base font-medium"
          data-testid="contact-submit-btn"
        >
          {status === 'loading' ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Send Inquiry
            </>
          )}
        </Button>
      </div>
    </motion.form>
  );
};

export default ContactForm;
